import { useEffect, useState } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Box,
  FormHelperText,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';

const useStyles = makeStyles(theme => ({
  formControl: {
    marginBottom: theme.spacing(2),
    width: '100%',
  },
  loadingBox: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}));

interface GitLabProject {
  id: number;
  path_with_namespace: string;
  name: string;
  default_branch: string;
  description: string;
}

interface GitLabProjectPickerProps {
  onChange: (value: {
    projectId: string;
    projectPath: string;
    owner: string;
    repo: string;
    defaultBranch: string;
  }) => void;
  rawErrors?: string[];
  required?: boolean;
  schema: {
    title?: string;
    description?: string;
  };
  formData?: any;
}

export const GitLabProjectPicker = ({
  onChange,
  rawErrors,
  required,
  schema,
  formData,
}: GitLabProjectPickerProps) => {
  const classes = useStyles();
  const [groups, setGroups] = useState<Array<{ id: number; path: string; name: string }>>([]);
  const [group, setGroup] = useState('');
  const [projects, setProjects] = useState<GitLabProject[]>([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [error, setError] = useState('');

  // 使用 Backstage 的 fetchApi (自动处理身份验证)
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  // 加载 Groups 列表
  useEffect(() => {
    const fetchGroups = async () => {
      setLoadingGroups(true);
      try {
        const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
        const response = await fetchApi.fetch(`${baseUrl}/gitlab-helper/groups`);

        if (!response.ok) {
          throw new Error(`Failed to fetch groups: ${response.statusText}`);
        }

        const data = await response.json();
        setGroups(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : '加载 Groups 失败');
        setGroups([]);
      } finally {
        setLoadingGroups(false);
      }
    };

    fetchGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 当 group 改变时，加载项目列表
  useEffect(() => {
    if (!group) {
      setProjects([]);
      return;
    }

    const fetchProjects = async () => {
      setLoading(true);
      setError('');

      try {
        const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
        const response = await fetchApi.fetch(`${baseUrl}/gitlab-helper/groups/${group}/projects`);

        if (!response.ok) {
          throw new Error(`Failed to fetch projects: ${response.statusText}`);
        }

        const data = await response.json();
        setProjects(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : '加载项目列表失败');
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group]);

  // 当选择项目时，获取详细信息并回调
  const handleProjectChange = async (projectId: string) => {
    setSelectedProject(projectId);

    const project = projects.find(p => p.id.toString() === projectId);
    if (!project) return;

    // 分割 path_with_namespace 为 owner 和 repo
    const pathParts = project.path_with_namespace.split('/');
    const owner = pathParts.slice(0, -1).join('/'); // 支持多层级 namespace
    const repo = pathParts[pathParts.length - 1];

    // 回调更新表单数据
    onChange({
      projectId: project.id.toString(),
      projectPath: project.path_with_namespace,
      owner: owner,
      repo: repo,
      defaultBranch: project.default_branch || 'main',
    });
  };

  return (
    <Box>
      {/* GitLab Group 选择 */}
      <FormControl
        className={classes.formControl}
        required={required}
        error={!!rawErrors && rawErrors.length > 0}
      >
        <InputLabel>GitLab Group</InputLabel>
        <Select
          value={group}
          onChange={e => setGroup(e.target.value as string)}
          disabled={loadingGroups}
        >
          {loadingGroups ? (
            <MenuItem value="">加载中...</MenuItem>
          ) : (
            groups.map(g => (
              <MenuItem key={g.id} value={g.path}>
                {g.name} ({g.path})
              </MenuItem>
            ))
          )}
        </Select>
        <FormHelperText>
          {loadingGroups ? '正在加载 GitLab Groups...' : '选择项目所属的 GitLab Group'}
        </FormHelperText>
      </FormControl>

      {/* 项目列表加载中 */}
      {loading && (
        <Box className={classes.loadingBox}>
          <CircularProgress size={20} />
          <span>加载项目列表中...</span>
        </Box>
      )}

      {/* 错误提示 */}
      {error && (
        <FormHelperText error>{error}</FormHelperText>
      )}

      {/* GitLab Project 选择 */}
      {group && projects.length > 0 && (
        <FormControl className={classes.formControl}>
          <InputLabel>GitLab Project</InputLabel>
          <Select
            value={selectedProject}
            onChange={e => handleProjectChange(e.target.value as string)}
          >
            {projects.map(project => (
              <MenuItem key={project.id} value={project.id.toString()}>
                {project.path_with_namespace}
                {project.description && ` - ${project.description}`}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            {selectedProject
              ? `已选择: ${projects.find(p => p.id.toString() === selectedProject)?.path_with_namespace}`
              : '从列表中选择 GitLab 项目'}
          </FormHelperText>
        </FormControl>
      )}

      {/* 显示选中项目的详细信息 */}
      {selectedProject && (
        <Box mt={2}>
          <TextField
            label="项目 ID"
            value={projects.find(p => p.id.toString() === selectedProject)?.id || ''}
            disabled
            fullWidth
            margin="dense"
          />
          <TextField
            label="项目路径"
            value={projects.find(p => p.id.toString() === selectedProject)?.path_with_namespace || ''}
            disabled
            fullWidth
            margin="dense"
          />
          <TextField
            label="默认分支"
            value={projects.find(p => p.id.toString() === selectedProject)?.default_branch || 'main'}
            disabled
            fullWidth
            margin="dense"
          />
        </Box>
      )}
    </Box>
  );
};

